// /finance — partner-level exposure with expandable project rollup.
//
// Top: total + per-partner cards (existing). Each partner row collapses
// or expands a project breakdown showing per-project committed and gap.
//
// Empty state per-partner: when a partner has no projects, the expanded
// section shows a conversational nudge to create one.

import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import {
  formatCAD,
  formatCADCompact,
  exposureStatusMeta,
  projectStatusMeta,
  type ProjectStatus,
  moneyToneCls,
} from '../lib/format';
import { PageFooter } from '../components/PageFooter';

interface ProfileExposure {
  profile_id: string;
  profile_name: string;
  profile_slug: string;
  matching_funds_ceiling: number | null;
  committed_in_progress_cad: number;
  open_application_count: number;
  utilization_pct: number | null;
  status: 'green' | 'amber' | 'red' | 'unset';
}
interface FinanceExposure {
  total_committed_in_progress_cad: number;
  total_open_applications: number;
  in_progress_stages: string[];
  profiles: ProfileExposure[];
}

interface ProjectRow {
  id: string;
  partner_profile_id: string;
  partner_name: string;
  name: string;
  ask_cad: number | null;
  match_available_cad: number | null;
  target_funding_date: number | null;
  status: ProjectStatus;
  application_count: number;
  match_count: number;
  committed_cad: number;
}

export default function FinancePage() {
  const navigate = useNavigate();
  const exposure = useQuery({
    queryKey: ['finance-exposure'],
    queryFn: () => api.get<FinanceExposure>('/dashboards/finance-exposure'),
  });
  const projects = useQuery({
    queryKey: ['projects', 'all'],
    queryFn: () => api.get<ProjectRow[]>('/projects'),
  });

  if (exposure.error instanceof ApiError && exposure.error.status === 401) {
    navigate({ to: '/welcome' });
    return null;
  }

  const data = exposure.data?.data;
  const projectsByPartner = new Map<string, ProjectRow[]>();
  for (const p of projects.data?.data ?? []) {
    const arr = projectsByPartner.get(p.partner_profile_id) ?? [];
    arr.push(p);
    projectsByPartner.set(p.partner_profile_id, arr);
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-5 py-5 space-y-5">
          <header>
            <h1 className="text-base font-semibold">Finance — matching-funds exposure</h1>
            <p className="mt-1 text-xs text-neutral-600">
              Total committed across all open applications (Drafting · Submitted · Awaiting). Won and lost applications are not counted as exposure. Expand a partner to see its project breakdown.
            </p>
          </header>

          {exposure.isLoading && <p className="text-sm text-neutral-600">Loading finance — a moment.</p>}

          {exposure.error && !(exposure.error instanceof ApiError && exposure.error.status === 401) && (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              <p className="font-medium">Couldn't load finance.</p>
              <p className="mt-1 text-xs">Refresh the page. If the problem persists, contact your project administrator.</p>
              <button onClick={() => exposure.refetch()} className="mt-2 text-xs underline hover:text-rose-900">Retry</button>
            </div>
          )}

          {data && (
            <>
              <section
                aria-label="Totals"
                className="border border-neutral-200 rounded-md px-4 py-3 bg-neutral-50"
              >
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  <div>
                    <dt className="text-xs text-neutral-600">Total committed (in-progress)</dt>
                    <dd className="text-xl font-semibold text-neutral-900 tabular-nums">
                      {formatCAD(data.total_committed_in_progress_cad)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-neutral-600">Open applications</dt>
                    <dd className="text-xl font-semibold text-neutral-900 tabular-nums">
                      {data.total_open_applications}
                    </dd>
                  </div>
                </dl>
              </section>

              <section aria-label="Per-partner exposure" className="space-y-3">
                {data.profiles.map((p) => (
                  <PartnerBar
                    key={p.profile_id}
                    row={p}
                    projects={projectsByPartner.get(p.profile_id) ?? []}
                  />
                ))}
              </section>

              <p className="text-[11px] text-neutral-500">
                Bars compare <em>match committed</em> against the partner's matching-funds ceiling.
                Green &lt; 70% · Amber 70 – 99% · Red ≥ 100%.
              </p>
            </>
          )}
        </div>
      </div>

      <PageFooter />
    </div>
  );
}

function PartnerBar({ row, projects }: { row: ProfileExposure; projects: ProjectRow[] }) {
  const [open, setOpen] = useState(false);
  const meta = exposureStatusMeta[row.status];
  const pct = row.utilization_pct;
  const widthPct = pct == null ? 0 : Math.min(100, pct);

  const activeProjects = projects.filter((p) => p.status === 'planning' || p.status === 'seeking' || p.status === 'funded');
  const projectAskTotal = activeProjects.reduce((acc, p) => acc + (p.ask_cad ?? 0), 0);
  const projectCommittedTotal = activeProjects.reduce((acc, p) => acc + p.committed_cad, 0);

  return (
    <article className="border border-neutral-200 rounded-md bg-white">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={`partner-${row.profile_id}-projects`}
        className="w-full text-left p-3 hover:bg-neutral-50/60"
      >
        <div className="flex items-center justify-between text-sm gap-3">
          <h2 className="font-medium text-neutral-900 flex items-center gap-2">
            <span aria-hidden="true" className="text-neutral-400 inline-block w-3">{open ? '▾' : '▸'}</span>
            {row.profile_name}
          </h2>
          <span className={'text-[11px] px-1.5 py-0.5 rounded ' + meta.chipCls}>{meta.label}</span>
        </div>
        <div className="mt-1 text-xs text-neutral-700 tabular-nums ml-5">
          {formatCAD(row.committed_in_progress_cad)} committed
          {row.matching_funds_ceiling != null && (
            <> of {formatCAD(row.matching_funds_ceiling)} ceiling{pct != null && ` · ${pct}%`}</>
          )}
          <span className="mx-1">·</span>
          {row.open_application_count} open application{row.open_application_count === 1 ? '' : 's'}
          {activeProjects.length > 0 && (
            <>
              <span className="mx-1">·</span>
              {activeProjects.length} active project{activeProjects.length === 1 ? '' : 's'}
            </>
          )}
        </div>
        {row.matching_funds_ceiling != null && (
          <div
            role="progressbar"
            aria-label={`${row.profile_name} matching-funds utilisation`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={widthPct}
            aria-valuetext={pct == null ? 'no ceiling set' : `${pct}% of ceiling`}
            className="mt-2 ml-5 h-2 bg-neutral-200 rounded overflow-hidden"
          >
            <div className={'h-full ' + meta.barCls} style={{ width: `${widthPct}%` }} />
          </div>
        )}
      </button>

      {open && (
        <div id={`partner-${row.profile_id}-projects`} className="border-t border-neutral-100 px-3 py-3 bg-neutral-50/40">
          {activeProjects.length === 0 ? (
            <p className="text-xs text-neutral-600">
              No active projects under {row.profile_name}.{' '}
              <Link to="/projects/new" className="underline text-accent">Create one →</Link>
            </p>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between text-[11px] text-neutral-600">
                <span>Active projects ({activeProjects.length})</span>
                <span className="tabular-nums">
                  Project asks <span className="text-neutral-900 font-medium">{formatCADCompact(projectAskTotal)}</span> ·
                  committed <span className="text-neutral-900 font-medium">{formatCADCompact(projectCommittedTotal)}</span>
                </span>
              </div>
              <ul className="space-y-1.5">
                {activeProjects.map((p) => <ProjectRowItem key={p.id} project={p} />)}
              </ul>
            </>
          )}
        </div>
      )}
    </article>
  );
}

function ProjectRowItem({ project }: { project: ProjectRow }) {
  const ask = project.ask_cad ?? 0;
  const committed = project.committed_cad;
  const gap = Math.max(0, ask - committed);
  const pct = ask > 0 ? Math.min(100, Math.round((committed / ask) * 100)) : 0;
  const status = projectStatusMeta[project.status];
  return (
    <li>
      <Link
        to="/projects/$id"
        params={{ id: project.id }}
        className="flex items-center gap-3 rounded-md border border-neutral-200 bg-white px-2.5 py-1.5 hover:border-neutral-300"
      >
        <span className="flex-1 min-w-0">
          <span className="text-sm text-neutral-900 truncate block">{project.name}</span>
          <span className="text-[11px] text-neutral-500 flex items-center gap-2">
            <span className={'inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ' + status.cls}>{status.label}</span>
            <span>{project.application_count} app{project.application_count === 1 ? '' : 's'}</span>
            <span>·</span>
            <span>{project.match_count} match{project.match_count === 1 ? '' : 'es'}</span>
          </span>
        </span>
        <span className="shrink-0 text-right text-[11px] tabular-nums">
          <span className="block text-neutral-700">Ask <span className="text-neutral-900">{formatCADCompact(ask)}</span></span>
          <span className="block text-neutral-700">
            Gap <span className={moneyToneCls(gap, 'gap')}>{formatCADCompact(gap)}</span>
          </span>
        </span>
        <span className="w-16 shrink-0">
          <div
            role="progressbar"
            aria-label={`Project funding ${pct}%`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            className="h-1.5 bg-neutral-200 rounded overflow-hidden"
          >
            <div className={'h-full ' + (gap === 0 ? 'bg-emerald-600' : 'bg-accent')} style={{ width: `${pct}%` }} />
          </div>
        </span>
      </Link>
    </li>
  );
}
